"use client";

import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { extractApiErrorMessage } from "../../../utils/error-handler";
import { editTrialEndDate } from "../../../services/billingService";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const toDateInputValue = (date) => {
  if (!date) return "";
  return new Date(date).toISOString().slice(0, 10);
};

const todayInputValue = () => new Date().toISOString().slice(0, 10);

const EditTrialModal = ({ isOpen, onClose, onSaved, schoolId, currentEndDate }) => {
  const [newEndDate, setNewEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setNewEndDate(toDateInputValue(currentEndDate));
      setReason("");
    }
  }, [isOpen, currentEndDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newEndDate < todayInputValue()) {
      toast.error("Date cannot be before today");
      return;
    }
    setSubmitting(true);
    try {
      await editTrialEndDate(schoolId, {
        newEndDate: new Date(newEndDate).toISOString(),
        reason: reason.trim() || undefined,
      });
      toast.success("Trial end date updated");
      onSaved?.();
      onClose();
    } catch (error) {
      toast.error(extractApiErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Trial End Date</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>New End Date *</Label>
            <Input
              type="date"
              min={todayInputValue()}
              value={newEndDate}
              onChange={(e) => setNewEndDate(e.target.value)}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Pick today to end the trial immediately, or a future date to extend it.
            </p>
          </div>
          <div>
            <Label>Reason (optional)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Optional reason" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditTrialModal;
